/**
 * Group-session statistics (`organizations/:id/booking-stats/attendance`,
 * permission `booking_events_read`).
 *
 * Two differences from `financeStats.api.ts`, both deliberate:
 *
 *  * this endpoint answers in the project's usual `{ data }` envelope, so the
 *    payload is unwrapped here — the finance endpoints return RAW bodies and
 *    their own file says so;
 *  * no `tz` parameter. The server buckets weekday/hour in the EVENT's stored
 *    zone so a recurring class does not split across two buckets when the
 *    clocks change, and sending the viewer's zone would neither move those
 *    buckets nor belong in the cache key.
 */
import type { BaseApiResponse } from './types/api.types';
import type {
  ApiBookingStatsAttendance,
  ApiBookingStatsRevenue,
  BookingStatsRangeArgs,
} from './types/bookingStats.types';

import { get } from './axios';

export const fetchBookingStatsAttendance = async ({
  from,
  organizationId,
  to,
}: BookingStatsRangeArgs) => {
  const response = await get<BaseApiResponse<ApiBookingStatsAttendance>>(
    `/v2/organizations/${organizationId}/booking-stats/attendance`,
    // The query DTO is `.strict()`: an extra parameter is refused outright,
    // so this object carries the period and nothing else.
    { query: { from, to } },
  );
  return response?.data ?? null;
};

/**
 * The full answer (`GET booking-stats`), read only for its `revenue` block.
 * The server adds that block for `finance_stats_read` (or a platform user) and
 * leaves it out otherwise, so `null` here means "not allowed to see the money",
 * never "no money".
 */
export const fetchBookingStatsRevenue = async ({
  from,
  organizationId,
  to,
}: BookingStatsRangeArgs) => {
  const response = await get<
    BaseApiResponse<{ revenue?: ApiBookingStatsRevenue }>
  >(`/v2/organizations/${organizationId}/booking-stats`, {
    query: { from, to },
  });
  return response?.data?.revenue ?? null;
};
